(() => {
  const DAY_IN_SECONDS = 86400;

  const getSectionRoot = (node) =>
    node.closest('product-info') || node.closest('.shopify-section') || document;

  const parseJsonScript = (id) => {
    const el = document.getElementById(id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const [year, month, day] = iso.split('-');
    if (!year || !month || !day) return iso;
    return `${day}.${month}.${year}`;
  };

  const formatShortDate = (iso) => {
    if (!iso) return '';
    const [year, month, day] = iso.split('-');
    if (!year || !month || !day) return iso;
    return `${day}.${month}.`;
  };

  const sanitizeDeliveryText = (text) => {
    if (!text) return '';
    return text
      .replace(/^Lieferzeit\s*:?\s*/i, '')
      .replace(/^Lieferung\s*:?\s*/i, '')
      .replace(/^Delivery time\s*:?\s*/i, '')
      .trim();
  };

  const normalizeDeliveryDays = (fromValue, toValue) => {
    let fromDays = Number.parseInt(fromValue ?? '', 10);
    let toDays = Number.parseInt(toValue ?? '', 10);

    if (Number.isNaN(fromDays) && !Number.isNaN(toDays)) fromDays = toDays;
    if (Number.isNaN(toDays) && !Number.isNaN(fromDays)) toDays = fromDays;

    if (!Number.isNaN(fromDays) && !Number.isNaN(toDays) && fromDays > toDays) {
      const swap = fromDays;
      fromDays = toDays;
      toDays = swap;
    }

    return { fromDays, toDays };
  };

  const addDays = (days) => {
    const safeDays = Number.parseInt(days, 10);
    if (Number.isNaN(safeDays)) return '';
    const date = new Date();
    date.setHours(12, 0, 0, 0);
    let remainingDays = safeDays;

    while (remainingDays > 0) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        remainingDays -= 1;
      }
    }

    if (date.getDay() === 0) {
      date.setDate(date.getDate() + 1);
    } else if (date.getDay() === 6) {
      date.setDate(date.getDate() + 2);
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const buildDisplay = (config, qty, variant) => {
    const nowScore = Math.floor(Date.now() / 1000);
    const stock = Number(variant?.inventory_quantity || 0);
    const isManaged = !!variant?.inventory_management;
    const isAvailable = !!variant?.available || !isManaged;
    const enoughStock = isAvailable && qty <= Math.max(0, stock);

    if (!variant) {
      return { hidden: true };
    }

    const fixedMode = config.fixedMode || '';
    const noteDefault = config.defaultNote || '';
    const pickupDefault = config.pickupText || '';
    let variantText = sanitizeDeliveryText(variant?.delivery_text || '');
    let variantNote = variant?.delivery_note || '';
    let { fromDays, toDays } = normalizeDeliveryDays(
      variant?.delivery_from_days ?? config.fromDays,
      variant?.delivery_to_days ?? config.toDays
    );
    let effectiveMode = fixedMode;
    const defaultFromDays = Number.parseInt(config.defaultFromDays ?? '3', 10);
    const defaultToDays = Number.parseInt(config.defaultToDays ?? '5', 10);
    const defaultLongFromDays = Number.parseInt(config.defaultLongFromDays ?? '10', 10);
    const defaultLongToDays = Number.parseInt(config.defaultLongToDays ?? '14', 10);
    const forceLongDefault = !!config.forceLongDefault;

    const makeResponse = (mode, text, note, showPickup, score, key, endIso = '') => ({
      hidden: false,
      mode,
      text,
      note: note || '',
      pickup: showPickup ? pickupDefault : '',
      showPickup,
      score,
      key,
      endIso,
    });

    if (variantText) {
      effectiveMode = 'text';
    } else if (!Number.isNaN(fromDays) || !Number.isNaN(toDays)) {
      effectiveMode = 'auto';
    }

    if (effectiveMode === 'text' && (variantText || config.customText)) {
      return makeResponse(
        'text',
        variantText || sanitizeDeliveryText(config.customText),
        variantNote || config.customNote,
        true,
        nowScore + 365 * DAY_IN_SECONDS,
        'custom-text'
      );
    }

    if (effectiveMode === 'auto' && !Number.isNaN(fromDays) && !Number.isNaN(toDays)) {
      const startIso = addDays(fromDays);
      const endIso = addDays(toDays);
      const note = variantNote || config.customNote || (toDays > defaultToDays ? noteDefault : '');

      if (fromDays === toDays) {
        return makeResponse(
          'until_date',
          formatDate(endIso),
          note,
          true,
          nowScore + toDays * DAY_IN_SECONDS,
          `auto-until-${toDays}`, endIso
        );
      }

      return makeResponse(
        'range',
        `${formatShortDate(startIso)}-${formatShortDate(endIso)}`,
        note,
        true,
        nowScore + toDays * DAY_IN_SECONDS,
        `auto-range-${fromDays}-${toDays}`, endIso
      );
    }

    if (effectiveMode === 'short') {
      const startIso = addDays(defaultFromDays);
      const endIso = addDays(defaultToDays);
      return makeResponse(
        'range',
        `${formatShortDate(startIso)}-${formatShortDate(endIso)}`,
        '',
        true,
        nowScore + defaultToDays * DAY_IN_SECONDS,
        `short-range-${defaultFromDays}-${defaultToDays}`, endIso
      );
    }

    if (effectiveMode === 'long') {
      const startIso = addDays(defaultLongFromDays);
      const endIso = addDays(defaultLongToDays);
      return makeResponse(
        'range',
        `${formatShortDate(startIso)}-${formatShortDate(endIso)}`,
        variantNote || config.customNote || config.longNote || noteDefault,
        true,
        nowScore + defaultLongToDays * DAY_IN_SECONDS,
        `long-range-${defaultLongFromDays}-${defaultLongToDays}`, endIso
      );
    }

    if (forceLongDefault) {
      const startIso = addDays(defaultLongFromDays);
      const endIso = addDays(defaultLongToDays);
      return makeResponse(
        'range',
        `${formatShortDate(startIso)}-${formatShortDate(endIso)}`,
        variantNote || config.customNote || config.longNote || noteDefault,
        true,
        nowScore + defaultLongToDays * DAY_IN_SECONDS,
        `forced-range-${defaultLongFromDays}-${defaultLongToDays}`, endIso
      );
    }

    if (enoughStock) {
      const startIso = addDays(defaultFromDays);
      const endIso = addDays(defaultToDays);
      return makeResponse(
        'range',
        `${formatShortDate(startIso)}-${formatShortDate(endIso)}`,
        '',
        true,
        nowScore + defaultToDays * DAY_IN_SECONDS,
        `default-range-${defaultFromDays}-${defaultToDays}`, endIso
      );
    }

    const startIso = addDays(defaultLongFromDays);
    const endIso = addDays(defaultLongToDays);
    return makeResponse(
      'range',
      `${formatShortDate(startIso)}-${formatShortDate(endIso)}`,
      variantNote || config.customNote || config.longNote || noteDefault,
      true,
      nowScore + defaultLongToDays * DAY_IN_SECONDS,
      `default-range-${defaultLongFromDays}-${defaultLongToDays}`, endIso
    );
  };

  const initDeliveryCard = (card) => {
    if (card.dataset.deliveryInitialized === 'true') return;
    card.dataset.deliveryInitialized = 'true';

    const configId = card.dataset.deliveryConfigId;
    const variantsId = card.dataset.deliveryVariantsId;
    const config = parseJsonScript(configId);
    const variants = parseJsonScript(variantsId) || [];
    if (!config) return;

    const sectionRoot = getSectionRoot(card);
    const getForm = () =>
      sectionRoot.querySelector('form[data-type="add-to-cart-form"]') ||
      document.querySelector(`form#${config.productFormId}`) ||
      document.querySelector('form[action*="/cart/add"]');

    const getVariant = () => {
      const idInput = getForm()?.querySelector('input[name="id"]');
      if (!idInput?.value) return null;
      return variants.find((variant) => String(variant.id) === String(idInput.value)) || null;
    };

    const getQuantity = () => {
      const qtyInput =
        sectionRoot.querySelector('input[name="quantity"]') ||
        getForm()?.querySelector('input[name="quantity"]');
      const qty = Number.parseInt(qtyInput?.value || '1', 10);
      return Number.isNaN(qty) || qty < 1 ? 1 : qty;
    };

    const textEl = card.querySelector('[data-delivery-text]');
    const noteEl = card.querySelector('[data-delivery-note]');
    const pickupEl = card.querySelector('[data-delivery-pickup]');
    const pillEl = card.querySelector('[data-delivery-pill]');
    const titleEl = card.querySelector('[data-delivery-title]');
    const stockSuggestion = window.FibroluxProductStock?.attach(card, config);
    const render = () => {
      const variant = getVariant();
      if (!variant) {
        stockSuggestion?.update(null, getQuantity(), null);
        card.hidden = true;
        if (noteEl) {
          noteEl.textContent = '';
          noteEl.hidden = true;
        }
        if (pickupEl) {
          pickupEl.textContent = '';
          pickupEl.hidden = true;
        }
        return;
      }

      const result = buildDisplay(config, getQuantity(), variant);
      stockSuggestion?.update(variant, getQuantity(), result);

      if (result.hidden) {
        card.hidden = true;
        return;
      }

      card.hidden = false;
      if (titleEl) {
        titleEl.textContent =
          result.mode === 'until_date' || result.mode === 'range' || result.mode === 'from_date'
            ? config.deliveryDateTitle || 'Voraussichtliches Zustelldatum:'
            : config.deliveryTimeTitle || 'Voraussichtliche Lieferzeit:';
      }
      if (textEl) textEl.textContent = result.text;
      if (noteEl) {
        // The concise stock status replaces only the repetitive default backorder paragraph.
        const note = stockSuggestion && variant.inventory_management && result.note === config.defaultNote ? '' : result.note;
        noteEl.textContent = note || '';
        noteEl.hidden = !note;
      }
      if (pickupEl) {
        pickupEl.textContent = config.pickupText || result.pickup || '';
        pickupEl.hidden = !(config.pickupText || result.pickup);
      }
      if (pillEl) {
        pillEl.textContent = config.categoryLabel || config.defaultKicker || 'Lieferzeit';
      }
    };

    document.addEventListener('change', () => window.setTimeout(render, 0));
    sectionRoot.addEventListener('input', () => window.setTimeout(render, 0));
    document.addEventListener('click', () => window.setTimeout(render, 0));
    ['variant:change', 'product:variant-change', 'theme:variant-change'].forEach((eventName) => {
      document.addEventListener(eventName, () => window.setTimeout(render, 0));
    });

    render();
  };

  const boot = () => {
    document.querySelectorAll('[data-fibrolux-delivery]').forEach(initDeliveryCard);
  };

  let bootScheduled = false;
  const scheduleBoot = () => {
    if (bootScheduled) return;
    bootScheduled = true;
    window.requestAnimationFrame(() => {
      bootScheduled = false;
      boot();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  ['variant:change', 'product:variant-change', 'theme:variant-change', 'shopify:section:load'].forEach((eventName) => {
    document.addEventListener(eventName, () => {
      scheduleBoot();
    });
  });

  const observer = new MutationObserver(() => {
    scheduleBoot();
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
